'use client';

import { useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import useSWR, { useSWRConfig } from 'swr';
import { ApiError, mcpApi } from '@/lib/api';
import type { McpSettingsDto } from '@/lib/api';
import { LONG_LIVED_SWR_OPTIONS, swrKeyStartsWith, useAuthedFetcher } from './swrConfig';

const KEY = 'mcp-settings';

// The plan and the connected apps are the person's own: keyed by user and never carried over to
// another account. Revalidates on focus, so the apps show the new use on the way back from Claude
// or ChatGPT. A 404 is an API without this endpoint yet: no retry, and the section stays hidden.
const OPTIONS = {
  ...LONG_LIVED_SWR_OPTIONS,
  keepPreviousData: false,
  shouldRetryOnError: (error: unknown) => !(error instanceof ApiError && error.status === 404),
};

export function useMcpSettings() {
  const { userId } = useAuth();
  const authedFetch = useAuthedFetcher();
  return useSWR<McpSettingsDto, Error>(
    userId ? [KEY, userId] : null,
    () => authedFetch((token) => mcpApi.settings(token)),
    OPTIONS,
  );
}

/**
 * Refetches the connector settings, for when the plan changes under them: once a checkout confirms
 * the subscription, a section loaded before the reconciliation would still ask for the upgrade.
 */
export function useRevalidateMcpSettings() {
  const { mutate } = useSWRConfig();
  return useCallback(() => mutate((key) => swrKeyStartsWith(key, [KEY])), [mutate]);
}
